import React, { FormEventHandler, useCallback, useRef, useState } from "react";
import { css } from "@emotion/react";
import cn from "classnames";
import { ReactComponent as ArrowSvg } from "../../assets/icons/Icon/DKT/Cart Coupon Code.svg";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { FONTS, fontSize } from "../../styles/fonts";
import { SwarovskiRadio } from "../../components/_shared-components/ui-elements/SwarovskiRadio";
import { NewsletterPopupT } from "./newsletter-popup";
import { InterpolateLinearlyReact } from "../../domain/translations/interpolate-linearly-react";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../components/T";
import { CSS_MQ } from "../../styles/breakpoints";

export const NewsletterPopupForm: ReactTFC<{
  t: NewsletterPopupT;
  links: { termsAndConditions: string; privacyPolicy: string };
  newsletterSignUp: (email: string) => Promise<unknown>;
}> = ({ t, links, newsletterSignUp }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();

    const email = inputRef.current?.value;

    if (email) {
      void newsletterSignUp(email);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;

          margin-bottom: 24px;

          &:focus-within {
            > ._button {
              background-color: ${CSS_COLORS.Green};
              color: ${CSS_COLORS.White};
            }
          }
        `}
      >
        <input
          ref={inputRef}
          placeholder={t("placeholder").value}
          type="email"
          css={[
            FONTS.MinervaModern500,
            css`
              margin: 0;
              padding: 0 18px;
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
              border: 1px solid black;
              height: 50px;

              font-size: 16px;

              min-width: 0;
              width: 100%;

              &:focus {
                outline: none !important;
                border: 1px solid ${CSS_COLORS.Green};
              }

              &::placeholder {
                color: ${CSS_COLORS.Grey3};
                ${FONTS.MinervaModern500};
              }
            `,
          ]}
        />
        <button
          className={cn("_button")}
          type={"submit"}
          css={css`
            height: 50px;

            width: 50px;
            ${CSS_MQ.atLeastMd} {
              width: 100px;
            }

            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
            border-top: 1px solid black;
            border-bottom: 1px solid black;
            border-right: 1px solid black;

            display: flex;
            justify-content: center;
            align-items: center;

            color: ${CSS_COLORS.Green};
            background-color: ${CSS_COLORS.White};
          `}
        >
          <ArrowSvg
            css={css`
              width: 25px;
            `}
          />
        </button>
      </div>

      <p
        css={[
          fontSize.x2,
          FONTS.SwarovskiFutura300,
          css`
            margin: 0;
            margin-top: -0.05em;
          `,
        ]}
      >
        <InterpolateLinearlyReact
          translationStringWithInterpolatedValues={`${t("newsletter.iAccept").value} <terms> ${t("newsletter.and").value} <privacyPolicy>.`}
          args={[
            <Link href={links.termsAndConditions} label={t("termsAndConditionsLinkLabel")} />,
            <Link href={links.privacyPolicy} label={t("privacyPolicyLinkLabel")} />,
          ]}
        />
      </p>
    </form>
  );
};

const Link: ReactTFC<{ href: string; label: TranslatedValue }> = props => (
  <a
    href={props.href}
    target={"_blank"}
    css={css`
      .disabled & {
        color: inherit;
      }

      color: ${CSS_COLORS.Green};

      font-weight: 500;
    `}
  >
    <T>{props.label}</T>
  </a>
);
